import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import * as activityActions from '../actions/activities.action';
import * as ongoingActivitiesActions from "../actions/ongoing-activities.action";
import * as ongoingActivityActions from '../actions/ongoing-activity.action';
import { LoadActivitiesSuccess, AddActivity } from "../actions";
import { ActivityAction, ActivityStatus, IActivity } from "../../activities/activity";
import { IWebsiteActivitiesState } from "../reducers/activities.reducer";
import { Utils } from "../../shared/utils/utils";
import { DbStoreState } from '../reducers';
import { Store } from '@ngrx/store';
import { getNotFinishedOngoingActivities } from '../selectors';
import { delay, filter, first, map, switchMap, tap } from 'rxjs/operators';
import {IWebsiteOngoingActivitiesState} from "../reducers/ongoing-activities.reducer";

@Injectable()
export class OngoingActivitiesEffects {

  loadActivitiesSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.LOAD_ACTIVITIES_SUCCESS),
    map((action: LoadActivitiesSuccess) => {
      const payload: { [websiteId: number]: IWebsiteActivitiesState } = action.payload;
      const websiteOngoingActivitiesMap: {
        [websiteId: number]: IWebsiteOngoingActivitiesState
      } = Object.keys(payload).reduce((websiteOngoingActivitiesMap, websiteId) => {
        const websiteOngoingActivities: IActivity[] = <any>Object.values(payload[websiteId].entities).filter((activity: IActivity): boolean => {
          return (<string[]>[
            ActivityStatus.PENDING,
            ActivityStatus.RUNNING
          ]).includes(activity.status);
        });

        websiteOngoingActivitiesMap[websiteId] = {
          websiteId: websiteId,
          entities: Utils.arrayToEntities(websiteOngoingActivities, 'id')
        };

        return websiteOngoingActivitiesMap;
      }, {});

      return new ongoingActivitiesActions.InitOngoingActivities(websiteOngoingActivitiesMap);
    })
  ));

  addOngoingActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.RUNNING,
      ActivityStatus.DONE,
      ActivityStatus.PENDING,
      ActivityStatus.FAILED,
      ActivityStatus.CANCELED,
    ]).includes(action.payload.status)),
    map((action: AddActivity) => {
      return new ongoingActivitiesActions.UpdateOngoingActivityState({
        websiteId: action.payload.websiteId,
        entity: action.payload
      });
    })
  ));

  removeCreateWebsiteInitialActiviy$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean =>
      (<string[]>[
        ActivityStatus.DONE,
        ActivityStatus.FAILED,
        ActivityStatus.CANCELED
      ]).includes(action.payload.status)
      && [ActivityAction.CREATE_WEBSITE, ActivityAction.CREATE_STAGING_WEBSITE, ActivityAction.MIGRATE_WEBSITE].includes(<any>action.payload.action)
    ),
    delay(1000),
    map((action: AddActivity) => {
      return new ongoingActivitiesActions.RemoveOngoingActivity({
        websiteId: 0,
        activityId: action.payload.id
      });
    })
  ));

  addFinishedOngoingActivity$ = createEffect(() => this._actions$.pipe(
    ofType(activityActions.ADD_ACTIVITY),
    filter((action: AddActivity): boolean => (<string[]>[
      ActivityStatus.DONE,
      ActivityStatus.FAILED,
      ActivityStatus.CANCELED
    ]).includes(action.payload.status)),
    delay(1000),
    map((action: AddActivity) => {
      return new ongoingActivitiesActions.FinishOngoingActivity({
        websiteId: action.payload.websiteId,
        entity: action.payload
      });
    })
  ));

  finishedOngoingActivity$ = createEffect(() => this._actions$.pipe(
    ofType(ongoingActivitiesActions.FINISH_ONGOING_ACTIVITY),
    tap(() => {
      this._store.dispatch(new ongoingActivityActions.UpdateOngoingActivity(null));
    }),
    delay(3000),
    switchMap(() => {
      return this._store.select(getNotFinishedOngoingActivities)
        .pipe(
          first(),
          filter((notFinishedOngoingActivities: IActivity[]): boolean => !notFinishedOngoingActivities.length),
          switchMap(() => {
            return [
              new ongoingActivitiesActions.FinishOngoingActivitiesSession(),
              new ongoingActivityActions.UpdateOngoingActivity(null),
            ];
          })
        );
    })
  ));

  constructor(
    private _store: Store<DbStoreState>,
    private _actions$: Actions
  ) {
  }

}
